import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

interface State {
    flexDirection: string;
    justifyContent: string;
    alignItems: string;
    flexWrap: string;
    gap: number;
    items: number;
    copied: boolean;
}

export default class FlexboxGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 12,
        items: 4,
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get cssCode(): string {
        const { flexDirection, justifyContent, alignItems, flexWrap, gap } = this.state;
        return `display: flex;\nflex-direction: ${flexDirection};\njustify-content: ${justifyContent};\nalign-items: ${alignItems};\nflex-wrap: ${flexWrap};\ngap: ${gap}px;`;
    }

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { flexDirection, justifyContent, alignItems, flexWrap, gap, items, copied } = this.state;
        const previewStyle: React.CSSProperties = {
            display: 'flex',
            flexDirection: flexDirection as any,
            justifyContent: justifyContent as any,
            alignItems: alignItems as any,
            flexWrap: flexWrap as any,
            gap: `${gap}px`,
            minHeight: 280,
        };
        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Container</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="fb-dir">flex-direction</label>
                        <select id="fb-dir" value={flexDirection}
                            onChange={e => this.setState({ flexDirection: e.target.value })}>
                            <option>row</option><option>row-reverse</option>
                            <option>column</option><option>column-reverse</option>
                        </select>
                    </div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="fb-just">justify-content</label>
                            <select id="fb-just" value={justifyContent}
                                onChange={e => this.setState({ justifyContent: e.target.value })}>
                                <option>flex-start</option><option>center</option><option>flex-end</option>
                                <option>space-between</option><option>space-around</option><option>space-evenly</option>
                            </select>
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="fb-align">align-items</label>
                            <select id="fb-align" value={alignItems}
                                onChange={e => this.setState({ alignItems: e.target.value })}>
                                <option>stretch</option><option>flex-start</option><option>center</option>
                                <option>flex-end</option><option>baseline</option>
                            </select>
                        </div>
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="fb-wrap">flex-wrap</label>
                        <select id="fb-wrap" value={flexWrap}
                            onChange={e => this.setState({ flexWrap: e.target.value })}>
                            <option>nowrap</option><option>wrap</option><option>wrap-reverse</option>
                        </select>
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Size</span></div>
                    <div className={genStyles.field}>
                        <label>gap: <strong>{gap}px</strong></label>
                        <input type="range" min={0} max={50} value={gap}
                            onChange={e => this.setState({ gap: +e.target.value })} />
                    </div>
                    <div className={genStyles.field}>
                        <label>items: <strong>{items}</strong></label>
                        <input type="range" min={1} max={12} value={items}
                            onChange={e => this.setState({ items: +e.target.value })} />
                    </div>
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                previewMeta={`${items} item${items === 1 ? '' : 's'}`}
                preview={
                    <div style={{ ...previewStyle, width: '100%', minHeight: 160 }}>
                        {Array.from({ length: items }, (_, i) => (
                            <div key={i} style={{ background: '#6366f1', color: '#fff', padding: '0.5rem 1rem', borderRadius: 6, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</div>
                        ))}
                    </div>
                }
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
